import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { APP_NAME, ENGINE_LABELS } from "@/config";
import { Page, PageHeader, Loading } from "@/components/common";
import { EngineMark } from "@/components/ProviderLogo";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Pencil } from "lucide-react";

// design_handoff_crewbench/screens/Models.dc.html: the model catalog by engine, its prices per 1M tokens and whether
// each model is enabled.
const FIELDS = [["input", "Input"], ["output", "Output"], ["cache_read", "Cache read"], ["cache_write", "Cache write 5m"], ["cache_write_1h", "Cache write 1h"]];
const price = (n) => (n == null ? "—" : `$${Number(n).toFixed(n < 1 ? 3 : 2).replace(/(\.\d\d)0$/, "$1")}`);
const COLS = "min-w-[1040px] grid grid-cols-[minmax(0,1.7fr)_repeat(6,minmax(84px,1fr))_76px_40px] gap-3 px-4";

export default function Models() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-models"], queryFn: async () => (await api.get("/admin/models")).data });
  const [edit, setEdit] = useState(null);
  const [prices, setPrices] = useState({});
  const [search, setSearch] = useState("");  // USD per 1,000 web searches; empty: the model can't search
  const [levels, setLevels] = useState("");  // the reasoning effort levels sessions may pick, comma separated
  const toggle = useMutation({
    mutationFn: async ({ m, enabled }) => api.put(`/admin/models/${m.id}`, { enabled }),
    onSuccess: (_r, { m, enabled }) => { qc.invalidateQueries({ queryKey: ["admin-models"] }); toast.success(`${m.model_id} ${enabled ? "enabled" : "disabled"}.`); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const savePrices = useMutation({
    mutationFn: async () => api.put(`/admin/models/${edit.id}`, {
      // a price left empty is left out (a missing 1-hour cache write price is billed at 2x input)
      prices_per_1m: Object.fromEntries(FIELDS.filter(([k]) => (prices[k] ?? "") !== "").map(([k]) => [k, Number(prices[k])])),
      web_search_per_1k: search === "" ? null : Number(search),
      effort_levels: levels.split(/[\s,]+/).map((l) => l.trim().toLowerCase()).filter(Boolean) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-models"] }); setEdit(null); toast.success("Prices updated."); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const title = "USD per 1M tokens, from the providers' price lists.";
  if (isLoading || !data) return <Page className="gap-5"><PageHeader eyebrow="Platform · Models and prices" title={title} /><Loading /></Page>;

  const models = data.models;
  const enabled = models.filter((m) => m.enabled).length;
  const engines = [...new Set(models.map((m) => m.engine))];
  return (
    <Page className="gap-5" data-testid="admin-models">
      <PageHeader eyebrow="Platform · Models and prices" title={title} status={`${enabled} of ${models.length} models are enabled.`} />
      <div className="bg-card rounded-[10px] overflow-x-auto text-sm tabular-nums pb-2">
        <div className={`${COLS} py-2.5 text-xs uppercase tracking-[0.09em] text-muted-foreground`}>
          <span>Model</span>{FIELDS.map(([k, l]) => <span key={k} className="text-right">{l}</span>)}
          <span className="text-right" title="USD per 1,000 web searches">Search /1K</span><span className="text-center">Enabled</span><span />
        </div>
        {engines.map((engine) => (
          <div key={engine}>
            <div className="min-w-[960px] flex items-center gap-2 px-4 pt-3.5 pb-1.5 border-t text-base font-semibold">
              <EngineMark engine={engine} className="w-3.5 h-3.5" />{ENGINE_LABELS[engine] || engine}
            </div>
            {models.filter((m) => m.engine === engine).map((m) => (
              <div key={m.id} className={`${COLS} py-2 items-center`} data-testid={`model-row-${m.model_id}`}>
                <div className="flex flex-col min-w-0 pl-[22px]">
                  <span className="text-base">{m.label}</span>
                  <span className="font-mono text-xs text-muted-foreground">{m.model_id}</span>
                  {m.effort_levels?.length > 0 && <span className="text-xs text-muted-foreground">Effort: {m.effort_levels.join(", ")}</span>}
                  {m.long_context && (  // OpenAI's rates for a request past its long-context threshold
                    <span className="text-muted-foreground" title="Set by the catalog, not editable here">
                      Over {Math.round(m.long_context.above_input_tokens / 1000)}K input tokens: {price(m.long_context.prices_per_1m.input)} in, {price(m.long_context.prices_per_1m.output)} out
                    </span>
                  )}
                </div>
                {FIELDS.map(([k]) => <span key={k} className="text-right">{price(m.prices_per_1m[k])}</span>)}
                <span className="text-right">{price(m.web_search_per_1k)}</span>
                <span className="flex justify-center">
                  <Switch checked={m.enabled} disabled={toggle.isPending} onCheckedChange={(v) => toggle.mutate({ m, enabled: v })}
                    aria-label={`${m.enabled ? "Disable" : "Enable"} ${m.model_id}`} data-testid={`model-toggle-${m.model_id}`} />
                </span>
                <Button size="icon" variant="ghost" className="justify-self-end" aria-label={`Edit prices for ${m.model_id}`} title="Edit prices"
                  onClick={() => { setEdit(m); setPrices(m.prices_per_1m); setSearch(m.web_search_per_1k ?? ""); setLevels((m.effort_levels || []).join(", ")); }} data-testid={`model-edit-${m.model_id}`}><Pencil /></Button>
              </div>
            ))}
          </div>
        ))}
      </div>
      <p className="m-0 text-sm text-muted-foreground">Usage on {APP_NAME} credits is charged at these prices plus the credit markup, set in Store → Settings.</p>
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="gap-4">
          <div className="flex flex-col gap-0.5">
            <DialogTitle>{edit?.label}</DialogTitle>
            <DialogDescription className="font-mono text-[12px] text-muted-foreground">{edit?.model_id}</DialogDescription>
          </div>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
            {FIELDS.map(([k, l]) => (
              <div key={k} className="flex flex-col gap-1.5">
                <label htmlFor={`price-${k}`} className="text-sm font-semibold">{l} per 1M</label>
                <span className="flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-gr-control-border focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring">
                  <span className="text-muted-foreground">$</span>
                  <input id={`price-${k}`} type="number" min="0" step="any" value={prices[k] ?? ""} onChange={(e) => setPrices({ ...prices, [k]: e.target.value })}
                    className="flex-1 min-w-0 border-0 bg-transparent text-[13px] outline-none tabular-nums" data-testid={`price-${k}`} />
                </span>
              </div>
            ))}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="price-web-search" className="text-sm font-semibold">Web search per 1K searches</label>
              <span className="flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-gr-control-border focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ring">
                <span className="text-muted-foreground">$</span>
                <input id="price-web-search" type="number" min="0" step="any" value={search} onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 min-w-0 border-0 bg-transparent text-[13px] outline-none tabular-nums" data-testid="price-web-search" />
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="effort-levels" className="text-sm font-semibold">Reasoning effort levels</label>
            <input id="effort-levels" value={levels} onChange={(e) => setLevels(e.target.value)} placeholder="low, medium, high"
              className="h-8 px-2.5 rounded-md border border-gr-control-border bg-transparent text-[13px]" data-testid="effort-levels" />
          </div>
          <span className="text-sm text-muted-foreground [text-wrap:pretty]">Leave a price empty to leave it out. Without a 1-hour cache write price, those writes are billed at 2× input. Without a web search price, the model can't search. Effort levels are what sessions may pick: list only the ones the provider takes for this model (e.g. xhigh, max).</span>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
            <Button disabled={savePrices.isPending} onClick={() => savePrices.mutate()} data-testid="model-save-btn">Save prices</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
